import { useState, type CSSProperties, type FC } from 'react';
import {
  canDownload,
  executeDownload,
  FILE_NOT_LINKED_TITLE,
} from '@/lib/download';
import type { EditorDocumentTemplate, EditorObject, EditorToolResult } from '../types';
import { CloseIcon, DownloadIcon, SparklesAIIcon } from './EditorIcons';

function templateDownloadFields(tpl: EditorDocumentTemplate) {
  return { file_path: tpl.file_path };
}

interface EditorDocToolsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  templates: EditorDocumentTemplate[];
  objects: EditorObject[];
  onOpenFiles: () => void;
  onRunTool: (payload: {
    tool_type: string;
    title: string;
    input_text?: string;
    linked_object_code?: string;
  }) => Promise<EditorToolResult>;
}

/** Documents & tools — cab-overlay chrome + cabinet-* body. */
export const EditorDocToolsOverlay: FC<EditorDocToolsOverlayProps> = ({
  isOpen,
  onClose,
  templates,
  objects,
  onOpenFiles,
  onRunTool,
}) => {
  const [selectedTool, setSelectedTool] = useState<string>('ANTIPLAGIARISM');
  const [toolInput, setToolInput] = useState<string>('');
  const [selectedObjectCode, setSelectedObjectCode] = useState<string>('ED-ART-041');
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [toolResult, setToolResult] = useState<EditorToolResult | null>(null);

  if (!isOpen) return null;

  const handleExecuteTool = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsRunning(true);
      const res = await onRunTool({
        tool_type: selectedTool,
        title:
          selectedTool === 'ANTIPLAGIARISM'
            ? 'Проверка на антиплагиат'
            : selectedTool === 'AI_DETECTOR'
              ? 'AI-детектор генеративного текста'
              : selectedTool === 'JOURNAL_FINDER'
                ? 'Подбор журналов Scopus / WoS'
                : 'Генератор библиографии',
        input_text: toolInput,
        linked_object_code: selectedObjectCode || undefined,
      });
      setToolResult(res);
    } finally {
      setIsRunning(false);
    }
  };

  const tools = [
    { id: 'ANTIPLAGIARISM', label: 'Антиплагиат' },
    { id: 'AI_DETECTOR', label: 'AI-детектор' },
    { id: 'JOURNAL_FINDER', label: 'Подбор журналов' },
    { id: 'BIBLIO_GENERATOR', label: 'Библиография' },
  ];

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Редактор проекта</small>
              <h2>Документы и инструменты</h2>
              <p>Шаблоны, файлы и исследовательские сервисы в одном контуре</p>
            </div>
          </div>
          <div className="cab-top-actions">
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="cab-body">
          <section className="cabinet-hero" style={{ '--accent': '#49ddb7' } as CSSProperties}>
            <div>
              <div className="cabinet-eyebrow">Рабочий центр</div>
              <h2>Документы и инструменты</h2>
              <p>Редакционные документы и инструменты собраны внутри одной панели.</p>
            </div>
            <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
              Роль: Редактор проекта
            </span>
          </section>

          <div className="cabinet-grid2" style={{ marginTop: 14 }}>
            <article className="cabinet-card" style={{ '--accent': '#49ddb7' } as CSSProperties}>
              <div className="cabinet-actions" style={{ marginBottom: 8 }}>
                <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
                  Документы
                </span>
                <span className="cabinet-spacer" />
                <span style={{ fontSize: 12, color: '#8faebb' }}>
                  {templates.length} активных шаблонов
                </span>
              </div>
              <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>Документы и шаблоны</h3>
              <p style={{ margin: '0 0 12px', color: '#8faebb', fontSize: 12, lineHeight: 1.5 }}>
                Программа, сборник, письма, сертификаты, формы рецензирования и служебные версии.
              </p>
              <button type="button" className="cabinet-btn primary" onClick={onOpenFiles}>
                Открыть файлы и документы
              </button>
              <div className="cabinet-stack" style={{ marginTop: 14 }}>
                {templates.map((tpl) => (
                  <div key={tpl.id} className="cabinet-note">
                    <div className="cabinet-actions">
                      <span
                        className="cabinet-badge"
                        style={{ '--badge': '#49ddb7' } as CSSProperties}
                      >
                        {tpl.template_code}
                      </span>
                      <span
                        className="cabinet-badge"
                        style={{ '--badge': '#8faebb' } as CSSProperties}
                      >
                        {tpl.version} · активна
                      </span>
                      <span className="cabinet-spacer" />
                      <button
                        type="button"
                        className="cabinet-btn"
                        disabled={!canDownload(templateDownloadFields(tpl))}
                        title={
                          canDownload(templateDownloadFields(tpl))
                            ? 'Скачать шаблон'
                            : FILE_NOT_LINKED_TITLE
                        }
                        aria-label={
                          canDownload(templateDownloadFields(tpl))
                            ? 'Скачать шаблон'
                            : FILE_NOT_LINKED_TITLE
                        }
                        onClick={() =>
                          void executeDownload(
                            templateDownloadFields(tpl),
                            tpl.file_name || tpl.title,
                          )
                        }
                      >
                        <DownloadIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <strong style={{ display: 'block', marginTop: 8 }}>{tpl.title}</strong>
                    <p style={{ margin: '4px 0 0', color: '#8faebb', fontSize: 11 }}>
                      {tpl.description}
                    </p>
                  </div>
                ))}
              </div>
            </article>

            <article className="cabinet-card" style={{ '--accent': '#e2ad3a' } as CSSProperties}>
              <div className="cabinet-actions" style={{ marginBottom: 8 }}>
                <span className="cabinet-badge" style={{ '--badge': '#e2ad3a' } as CSSProperties}>
                  Инструменты
                </span>
                <span className="cabinet-spacer" />
                <span style={{ fontSize: 12, color: '#8faebb' }}>Набор сервисов исследования</span>
              </div>
              <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>Программы и инструменты</h3>
              <p style={{ margin: '0 0 12px', color: '#8faebb', fontSize: 12, lineHeight: 1.5 }}>
                Опросы, эксперименты и анализ данных через модуль инструментов редакции.
              </p>

              <form onSubmit={handleExecuteTool} className="cabinet-stack">
                <div className="cabinet-filters" style={{ flexWrap: 'wrap' }}>
                  {tools.map((tool) => (
                    <button
                      key={tool.id}
                      type="button"
                      className={`cabinet-filter${selectedTool === tool.id ? ' active' : ''}`}
                      onClick={() => setSelectedTool(tool.id)}
                    >
                      {tool.label}
                    </button>
                  ))}
                </div>

                <label className="cabinet-field">
                  <span>Связать результат с объектом</span>
                  <select
                    className="cabinet-select"
                    value={selectedObjectCode}
                    onChange={(e) => setSelectedObjectCode(e.target.value)}
                  >
                    <option value="">Без привязки к объекту</option>
                    {objects.map((o) => (
                      <option key={o.id} value={o.object_code}>
                        {o.object_code} · {o.title}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="cabinet-field">
                  <span>Текст или фрагмент рукописи для анализа</span>
                  <textarea
                    className="cabinet-textarea"
                    rows={3}
                    value={toolInput}
                    onChange={(e) => setToolInput(e.target.value)}
                    placeholder="Вставьте аннотацию, текст или метаданные…"
                  />
                </label>

                <button type="submit" className="cabinet-btn primary" disabled={isRunning}>
                  <SparklesAIIcon className="w-4 h-4" />
                  {isRunning ? 'Выполняется анализ…' : 'Запустить инструмент'}
                </button>
              </form>

              {toolResult ? (
                <div className="cabinet-note" style={{ marginTop: 12 }}>
                  <div className="cabinet-actions">
                    <strong>{toolResult.title}</strong>
                    <span className="cabinet-badge" style={{ '--badge': '#8faebb' } as CSSProperties}>
                      {toolResult.linked_object_code || 'Общий контур'}
                    </span>
                  </div>
                  <p style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.5 }}>
                    {toolResult.output_summary}
                  </p>
                </div>
              ) : null}
            </article>
          </div>
        </div>
      </div>
    </div>
  );
};
